import { Link } from "@tanstack/react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

type PaginationProps = {
  currentPage: number;
  pageCount: number;
  pageSize: number;
  total: number;
};

export default function Pagination({ currentPage, pageCount, total }: PaginationProps) {
  if (pageCount <= 1) return null;

  // Generate page numbers
  const pages: (number | string)[] = [];
  if (pageCount <= 7) {
    for (let i = 1; i <= pageCount; i++) {
      pages.push(i);
    }
  } else {
    if (currentPage <= 4) {
      pages.push(1, 2, 3, 4, 5, "...", pageCount);
    } else if (currentPage >= pageCount - 3) {
      pages.push(1, "...", pageCount - 4, pageCount - 3, pageCount - 2, pageCount - 1, pageCount);
    } else {
      pages.push(1, "...", currentPage - 1, currentPage, currentPage + 1, "...", pageCount);
    }
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-10 border-t border-border">
      <p className="text-sm text-muted-foreground">
        Page <span className="font-semibold text-foreground">{currentPage}</span> of{" "}
        <span className="font-semibold text-foreground">{pageCount}</span>{" "}
        <span className="hidden sm:inline">({total} articles)</span>
      </p>

      <nav className="flex items-center gap-1.5" aria-label="Pagination">
        {/* Previous */}
        <Link
          from="/blog"
          search={(prev) => ({ ...prev, page: Math.max(1, currentPage - 1) })}
          disabled={currentPage <= 1}
          className={cn(
            "inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border bg-background text-foreground hover:bg-secondary transition-colors",
            currentPage <= 1 && "opacity-40 pointer-events-none"
          )}
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </Link>

        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {pages.map((p, i) =>
            typeof p === "number" ? (
              <Link
                key={i}
                from="/blog"
                search={(prev) => ({ ...prev, page: p })}
                className={cn(
                  "inline-flex items-center justify-center w-9 h-9 rounded-lg text-sm font-medium transition-all",
                  currentPage === p
                    ? "bg-primary-gradient text-primary-foreground shadow-sm shadow-primary/20"
                    : "border border-border bg-background text-foreground hover:bg-secondary"
                )}
                aria-current={currentPage === p ? "page" : undefined}
              >
                {p}
              </Link>
            ) : (
              <span key={i} className="px-1.5 text-muted-foreground select-none">
                …
              </span>
            )
          )}
        </div>

        {/* Next */}
        <Link
          from="/blog"
          search={(prev) => ({ ...prev, page: Math.min(pageCount, currentPage + 1) })}
          disabled={currentPage >= pageCount}
          className={cn(
            "inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border bg-background text-foreground hover:bg-secondary transition-colors",
            currentPage >= pageCount && "opacity-40 pointer-events-none"
          )}
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </Link>
      </nav>
    </div>
  );
}
